import React, { useState, useMemo, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Snackbar,
    Alert,
    TextField,
    InputAdornment,
    Fab,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    ToggleButtonGroup,
    ToggleButton,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    Edit as EditIcon,
    Add as AddIcon,
    Block as BlockIcon,
    Visibility as VisibilityIcon,
    PrecisionManufacturing as PrecisionManufacturingIcon,
    Engineering as EngineeringIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
} from "@mui/icons-material";

import VehicleForm from "./VehicleForm";
import { useVehicles } from "../../hooks/useVehicles";
import { useOrganizations } from "../../hooks/useOrganizations";
import { useAuthContext } from "../../context/AuthProvider";
import { useNavigate } from "react-router-dom";

const VehiclesDashboard = () => {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [editVehicle, setEditVehicle] = useState(null);
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [openConfirm, setOpenConfirm] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    const ADMIN_ID = Number(process.env.REACT_APP_ROL_ADMIN_ID);
    const { user } = useAuthContext();
    const { vehicles = [], isLoading, error, createMutation, updateMutation, deleteMutation } = useVehicles();
    const { organizations } = useOrganizations();

    const showAlert = useCallback((message, severity = "success") => {
        setAlert({ open: true, message, severity });
    }, []);

    const handleOpenCreate = useCallback(() => {
        setEditVehicle(null);
        setOpen(true);
    }, []);

    const handleOpenEdit = useCallback((vehicle) => {
        setEditVehicle(vehicle);
        setOpen(true);
    }, []);

    const handleCloseForm = useCallback(() => {
        setOpen(false);
        setEditVehicle(null);
    }, []);

    const handleOpenConfirm = useCallback((vehicle) => {
        setSelectedVehicle(vehicle);
        setOpenConfirm(true);
    }, []);

    const handleCloseConfirm = useCallback(() => {
        setOpenConfirm(false);
        setSelectedVehicle(null);
    }, []);

    const handleSubmitVehicle = useCallback((data) => {
        if (!user) return showAlert("Debe iniciar sesión", "error");

        if (editVehicle) {
            updateMutation.mutate({ id: editVehicle.vehid, data: { ...data, updbyid: user.EmpID } }, {
                onSuccess: () => { showAlert("Vehículo actualizado"); handleCloseForm(); },
                onError: (err) => showAlert(err?.message || "Error al actualizar", "error"),
            });
        } else {
            createMutation.mutate({ ...data, crebyid: user.EmpID }, {
                onSuccess: () => { showAlert("Vehículo creado"); handleCloseForm(); },
                onError: (err) => showAlert(err?.message || "Error al crear", "error"),
            });
        }
    }, [user, editVehicle, updateMutation, createMutation, showAlert, handleCloseForm]);

    const handleConfirmDelete = useCallback(() => {
        if (selectedVehicle) {
            deleteMutation.mutate(selectedVehicle.vehid, {
                onSuccess: () => { showAlert("Vehículo eliminado"); handleCloseConfirm(); },
                onError: (err) => { showAlert(err?.message || "Error al eliminar", "error"); handleCloseConfirm(); },
            });
        }
    }, [selectedVehicle, deleteMutation, showAlert, handleCloseConfirm]);

    const normalizedSearch = search.trim().toLowerCase();

    const rows = useMemo(() => {
        let data = vehicles;

        if (statusFilter === "ACTIVO") data = data.filter((v) => v.vehest === "ACTIVO");
        else if (statusFilter === "INACTIVO") data = data.filter((v) => v.vehest !== "ACTIVO");

        if (normalizedSearch) {
            data = data.filter((v) => {
                const mat = (v.vehmat || "").toLowerCase();
                const mar = (v.vehmar || "").toLowerCase();
                const mod = (v.vehmod || "").toLowerCase();
                const org = (v.orgnom || "").toLowerCase();
                return [mat, mar, mod, org].some(f => f.includes(normalizedSearch));
            });
        }
        return data;
    }, [vehicles, statusFilter, normalizedSearch]);

    const columns = useMemo(() => [
        {
            field: "orgnom", headerName: "Empresa", flex: 1, minWidth: 150,
            valueGetter: (params) => params.row?.orgnom || "N/A",
            renderCell: (params) => {
                const orgNom = params.value;
                const orgNomSoc = params.row?.orgnomsoc;
                return orgNom !== "N/A" && orgNomSoc ? (
                    <Tooltip title={orgNomSoc} arrow placement="top">
                        <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>{orgNom}</Box>
                    </Tooltip>
                ) : orgNom;
            },
        },
        {
            field: "ownershiptype", headerName: "Tenencia", width: 130,
            renderCell: (params) => {
                const map = {
                    OWNED: { label: "Propio", color: "success" },
                    CUSTOMER: { label: "Cliente", color: "info" },
                    LEASED: { label: "Leasing", color: "warning" },
                    MANAGED: { label: "Administrado", color: "secondary" },
                    THIRD_PARTY: { label: "Tercero", color: "default" },
                };
                const cfg = map[params.value] || { label: params.value || '—', color: 'default' };
                return <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />;
            },
        },
        { field: "vehmat", headerName: "Matrícula", width: 130 },
        { field: "vehmar", headerName: "Marca", width: 140 },
        { field: "vehmod", headerName: "Modelo", width: 140 },
        { field: "vehcap", headerName: "Capacidad", width: 110, valueGetter: (params) => params.row?.vehcap || "N/R" },
        {
            field: "vehkmactual", headerName: "Kilometraje", width: 130,
            valueGetter: (params) => params.row?.vehkmactual != null ? Number(params.row.vehkmactual).toFixed(1) : "N/R",
        },
        {
            field: "vehest", headerName: "Estado", width: 120, sortable: true,
            renderCell: (params) => {
                const isActive = params.value === "ACTIVO";
                return <Chip label={isActive ? "ACTIVO" : "INACTIVO"} color={isActive ? "success" : "warning"} size="small" variant="outlined" />;
            },
        },
        {
            field: "actions", headerName: "Acciones", width: 200, sortable: false, filterable: false, align: "center", headerAlign: "center",
            renderCell: (params) => {
                const vehicle = params.row;
                const canDelete = vehicle.estreg !== "E" && user?.RolID === ADMIN_ID;
                return (
                    <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Editar"><IconButton size="small" onClick={() => handleOpenEdit(vehicle)}><EditIcon color="primary" fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Ver Detalles"><IconButton size="small" color="info" onClick={() => navigate(`/administracion/vehiculo/${vehicle.vehid}`)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Componentes"><IconButton size="small" color="secondary" onClick={() => navigate(`/operaciones/vehiculo/${vehicle.vehid}/componentes`)}><PrecisionManufacturingIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Mantenimiento"><IconButton size="small" color="success" onClick={() => navigate(`/operaciones/vehiculo/${vehicle.vehid}/mantenimientos`)}><EngineeringIcon fontSize="small" /></IconButton></Tooltip>
                        {canDelete && (
                            <Tooltip title="Eliminar"><IconButton size="small" onClick={() => handleOpenConfirm(vehicle)} disabled={deleteMutation.isPending}><BlockIcon color="error" fontSize="small" /></IconButton></Tooltip>
                        )}
                    </Stack>
                );
            },
        },
    ], [ADMIN_ID, user?.RolID, handleOpenEdit, navigate, handleOpenConfirm, deleteMutation.isPending]);

    return (
        <Paper sx={{ p: 2.5, borderRadius: 2, height: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f9f9f9" }}>
            <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={700}>Vehículos</Typography>
                    <ToggleButtonGroup size="small" exclusive value={statusFilter} onChange={(_, val) => val && setStatusFilter(val)}>
                        <ToggleButton value="ALL">Todos</ToggleButton>
                        <ToggleButton value="ACTIVO">Activos</ToggleButton>
                        <ToggleButton value="INACTIVO">Inactivos</ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <TextField
                    size="small" label="Buscar por Matrícula, Marca, Modelo o Empresa..." variant="outlined" fullWidth
                    value={search} onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <Tooltip title="Limpiar"><IconButton size="small" onClick={() => setSearch("")}><ClearIcon fontSize="small" /></IconButton></Tooltip>
                            </InputAdornment>
                        ) : null,
                    }}
                />

                {error && <Alert severity="error">{error.message || "Error al cargar los vehículos"}</Alert>}

                <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
                    <DataGrid
                        rows={rows} columns={columns} loading={isLoading} getRowId={(row) => row.vehid} disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50, 100]}
                        initialState={{ pagination: { paginationModel: { pageSize: 10, page: 0 } }, sorting: { sortModel: [{ field: "vehmat", sort: "asc" }] } }}
                        sx={{ borderRadius: 2, backgroundColor: "#fff", "& .MuiDataGrid-columnHeaders": { fontWeight: 700, backgroundColor: "#f0f0f0" } }}
                    />
                </Box>
            </Stack>

            <Fab color="primary" aria-label="add" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={handleOpenCreate}><AddIcon /></Fab>

            {open && <VehicleForm open={open} handleClose={handleCloseForm} onSubmit={handleSubmitVehicle} defaultValues={editVehicle || {}} organizations={organizations} />}

            <Dialog open={openConfirm} onClose={handleCloseConfirm}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el vehículo con matrícula <strong>{selectedVehicle?.vehmat}</strong>? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirm} color="inherit">Cancelar</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error" disabled={deleteMutation.isPending}>
                        {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert((a) => ({ ...a, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "right" }}>
                <Alert onClose={() => setAlert((a) => ({ ...a, open: false }))} severity={alert.severity} variant="filled" sx={{ width: "100%" }}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default VehiclesDashboard;